import Vue from "vue";
import { actionItemUserRolesMap } from "../user/user-labels";
import session from '../framework/utils/session-store';

const store = {
    namespaced: true,
    state: {
        items: [],
        itemsCount: null,
        selectedItems: [],
        currentSearchParams: null,
    },
    apiGetters: {
        actionItemTypes: "/actionItem/v-2/types",
        actionItemRoles:"/actionItem/v-2/roles",
    },
    actions: {
        async refresh({ dispatch, state }) {
          if (state.currentSearchParams) {
            dispatch('search', state.currentSearchParams);
          } else {
            dispatch('loadAssigned');
          }
        },
        async loadAssigned(context) {
            let r = await romeApi.get('/actionitem/v-2/assigned');
            context.commit("setItems", r);
            context.commit("setItemsCount", r.length);
            context.commit("setSearchParams", null);
            context.state.selectedItems = [];

            if(context.state.items.length == 1 && !session.state.user.isInternalUser){
                context.dispatch('expandItem',context.state.items[0].actionItemId);
            }
        },
        async loadAllForRole(context, data) {
          let r = await romeApi.get('/actionitem/v-2/allForRole', data);
          context.commit("setItems", r);
          context.commit("setItemsCount", r.length);
          context.commit("setSearchParams", null);
          context.state.selectedItems = [];
        },
        async loadCount(context) {
            const count = await romeApi.get('/actionitem/v-2/assignedcount');
            context.commit("setItemsCount", count);
        },
        clearMultiSelection({state}) {
            state.selectedItems = [];
        },
        toggleMultiSelection({state}, actionItem) {
          if (state.selectedItems.includes(actionItem.actionItemId)) {
            state.selectedItems = state.selectedItems.filter(i => i != actionItem.actionItemId);
          } else {
            state.selectedItems.push(actionItem.actionItemId);
          }
        },
        async loadDetails(context, actionItemId) {
            if (!actionItemId) return;

            let r = await romeApi.get(`/actionitem/v-2/${actionItemId}`);
            let [history, workflowOrder] = await Promise.all([
                getHistory(actionItemId),
                getWorkflowOrder(r)
            ]);
            r.orderEffectiveDate = workflowOrder && workflowOrder.orderEffectiveDate;
            r.isRenewal = workflowOrder && workflowOrder.isRenewalOrder;
            setAssignmentText(r);
            context.commit("setDetails", r);
            context.commit("setHistory", { actionItemId, history });
        },
        async collapseItem(context, actionItemId) {
            await context.commit("setExpanded", { actionItemId, expanded: false });
        },
        async expandItem(context, actionItemId) {
            await context.dispatch("loadDetails", actionItemId);
            await context.commit("setExpanded", { actionItemId, expanded: true });
            await context.dispatch("clearMultiSelection");
        },
        async search(context, data) {
            const items = await romeApi.post(`/actionitem/v-2/search/`, data);
            context.commit("setItems", items);
            await context.dispatch('loadCount');
            context.commit("setSearchParams", data);
            context.state.selectedItems = [];
        },
        async changeStatus({ commit, dispatch, state }, data) {
            let newStatus = statusToInt(data.newStatus);

            if (!data.actionItem && data.actionItemId)
                data.actionItem = byId(state, data.actionItemId);

            if (!data.actionItem) throw "changeStatus: Action item is not specified";

            const url = getApiUrlForStatus(data.actionItem, newStatus);
            var res = await romeApi.post(url, { notes: data.notes, overrideValidation: data.overrideValidation });

            if (res && res.message && res.message.length > 0) {
              await modalService.showResult(res);
            } else {
              showToast(newStatus, data.toastText);
            }

            dispatch("refresh");
            if ((newStatus === statusEnum.completed || newStatus === statusEnum.approved) && data.onComplete) {
              dispatch(data.onComplete, null , { root: true });
            }
        },
        async addComment(context, data){
            await romeApi.post(`/actionitem/v-2/${data.actionItemId}/comment`,{text: data.comment});
            let history = await getHistory(data.actionItemId);
            context.commit("setHistory", { actionItemId: data.actionItemId, history });
        },
        async assignToUser(context, data)
        {
            await romeApi.post(`/actionitem/v-2/${data.actionItemId}/assign/${data.userId}`);
            context.commit('setDetails', {
              actionItemId: data.actionItemId,
              assignmentText: data.assignmentText
            });
        },
        async assignToRole(context, data)
        {
            await romeApi.post(`/actionitem/v-2/${data.actionItemId}/assignrole/${data.roleId}`);
            context.commit('setDetails', {
              actionItemId: data.actionItemId,
              assignmentText: data.assignmentText
            });
        },
        async batchAssignToUser(context, userId)
        {
          await romeApi.post(`/actionitem/v-2/batch-assign-user`, { actionItemIds: context.state.selectedItems, userId });
          await context.dispatch('refresh');
        },
        async batchAssignToRole(context, roleId)
        {
          await romeApi.post(`/actionitem/v-2/batch-assign-role`, {actionItemIds: context.state.selectedItems, roleId });
          await context.dispatch('refresh');
        },
        async resetAssignment({state}, data) {
            await romeApi.post(`/actionitem/v-2/${data.actionItemId}/resetassignment`);
        },
        async validateCanChangeStatus({state}, data) {
            return await romeApi.post(`/actionitem/v-2/${data.actionItem.actionItemId}/validate`);
        },
        async uploadAttachments(context, data) {
            await romeApi.upload('actionitem/v-2/upload', data.formData);
            await context.dispatch("loadDetails", data.actionItemId);
        }
    },
    getters: {
        multiSelectedCount(state) {
            return state.selectedItems.length;
        },
        hasExpanded(state) {
            return !!_.find(state.items, i => !!i.expanded);
        },
    },
    mutations: {
        setItems(state, data) {
            data.forEach(item => {
                setAssignmentText(item);
                item.expanded = false;
                item.comments = [];
                item.files = [];
                item.orderEffectiveDate = null;
                item.isRenewal = null;
            });
            state.items = data;
        },
        setItemsCount(state, data) {
            state.itemsCount = data;
        },
        setSearchParams(state, data) {
            state.currentSearchParams = data;
        },
        setFromSearch(state, flag) {
            state.fromSearch = flag;
        },
        setDetails(state, itemDetails) {
            var item = byId(state, itemDetails.actionItemId);
            _.keys(itemDetails).forEach(k => item[k] = itemDetails[k]);
        },
        setHistory(state, data) {
            var item = byId(state, data.actionItemId);
            Vue.set(item, 'history', data.history);
        },
        setExpanded(state, data) {
            byId(state, data.actionItemId).expanded = data.expanded;
        },
        collapseAll(state) {
            state.items.forEach(i => i.expanded = false);
        }
    }
};

const statusEnum = {
    open: 1,
    approved: 2,
    rejected: 3,
    completed: 4,
    cancelled: 5
}

function byId(state, actionItemId) {
    return _.find(state.items, i => i.actionItemId == actionItemId);
}

function setAssignmentText(item) {
    item.assignmentText =
      item.assignments ?
        item.assignments.length > 1 ?
            _(item.assignments)
              .take(2)
              .map(a => a.userName || a.roleName || a.relation)
              .map(x => actionItemUserRolesMap[x] ? actionItemUserRolesMap[x].name : x)
              .map(a => a.replace('employer.', ''))
            .join(", ") :
            item.assignments
              .map(a => a.userName || a.roleName || a.relation)
              .map(x => actionItemUserRolesMap[x] ? actionItemUserRolesMap[x].name : x)
              .join(", ")
            : "none";
    if (item.assignments && item.assignments.length > 2) item.assignmentText += "...";
}

function statusToInt(status) {
    if (_.isString(status)) {
        if (statusEnum[status])
            return statusEnum[status];
        else
            throw "Unknown status " + status;
    } else {
        return status
    }
}

function getApiUrlForStatus(actionItem, newStatus) {
    if (newStatus == statusEnum.approved) {
        return actionItem.approveUrl || `/actionitem/v-2/${actionItem.actionItemId}/approve`;
    } else if (newStatus == statusEnum.completed) {
        return actionItem.approveUrl || actionItem.completeUrl || `/actionitem/v-2/${actionItem.actionItemId}/complete`;
    } else if (newStatus == statusEnum.rejected) {
        return actionItem.rejectUrl || `/actionitem/v-2/${actionItem.actionItemId}/reject`;
    } else {
        throw "No API URL for this status";
    }
}

function showToast(newStatus, toastText) {
    let type = "info";

    if (newStatus == statusEnum.rejected) {
        type = "error";
        toastText = toastText || "Action item has been rejected";
    }  else {
        toastText = toastText || "Action item has been completed";
    }

    toastService[type](toastText);
}

async function getHistory(actionItemId) {
    var history = await romeApi.get(`/actionitem/v-2/${actionItemId}/history`);
    history = history.map(h => {
        h.isComment = h.bag.type == 4;
        h.isFile = h.bag.storageFile != null;
        h.link = h.isFile ? `actionitem/${actionItemId}/attachments/${h.bag.storageFileId}` : null;
        return h;
    });


    for(var item of history) {
        if(item.isComment) {
            item.isFirst = true;
            break;
        }
    }

    return history;
}

async function getWorkflowOrder(actionItem) {
    if (!actionItem.workflowId) return null;
    return await romeApi.get(`/workflow/${actionItem.workflowId}/order`);
}
export default store;



// WEBPACK FOOTER //
// ./src/action-items/x-action-items.js